import { CALCULATOR_DEFAULTS } from '../constants.js';
import { calculateBlend } from '../blend/engine.js';
import type { BlendResult } from '../types.js';
import { filterBalancingLabelEligible, hasValidWheelThc } from './eligibility.js';
import { pickRandomStrain, type RandomFn } from './selection.js';
import type {
  WheelBlendWeights,
  WheelStrainCandidate,
  WheelTargets,
} from './types.js';

function isFinitePositiveWeight(n: number, total: number): boolean {
  return (
    Number.isFinite(n) &&
    n >= 0 &&
    n <= total + 1e-9 &&
    !Number.isNaN(n)
  );
}

/**
 * Whether a blend result is a usable two-strain solution for the wheel:
 * target in reachability range, both weights positive, no impossible values.
 * Closest-mode rounding is allowed (exact mg match is not required).
 */
export function isValidWheelBlendResult(
  result: BlendResult | null | undefined,
  totalWeightGrams: number,
  targetOutOfRange: boolean,
): result is BlendResult {
  if (!result) return false;
  if (targetOutOfRange) return false;
  if (!Number.isFinite(result.estimatedThcMg)) return false;
  if (!Number.isFinite(result.differenceMg)) return false;
  if (result.allocations.length < 2) return false;
  let sum = 0;
  for (const a of result.allocations) {
    if (!isFinitePositiveWeight(a.grams, totalWeightGrams)) return false;
    if (!(a.grams > 0)) return false;
    sum += a.grams;
  }
  if (Math.abs(sum - totalWeightGrams) > 1e-6) return false;
  return true;
}

/**
 * Compute two-strain blend weights using the existing blend engine.
 */
export function calculateWheelBlend(
  primary: WheelStrainCandidate,
  balancer: WheelStrainCandidate,
  targets: WheelTargets,
):
  | { ok: true; weights: WheelBlendWeights; result: BlendResult }
  | { ok: false; message: string } {
  if (!hasValidWheelThc(primary.thcPercent) || !hasValidWheelThc(balancer.thcPercent)) {
    return { ok: false, message: 'Both strains need valid THC values.' };
  }
  if (primary.id === balancer.id) {
    return { ok: false, message: 'Balancing strain must differ from the primary.' };
  }

  const outcome = calculateBlend({
    strains: [
      {
        strainId: primary.id,
        name: primary.name ?? primary.productName ?? 'Primary',
        thcPercent: primary.thcPercent,
      },
      {
        strainId: balancer.id,
        name: balancer.name ?? balancer.productName ?? 'Balancer',
        thcPercent: balancer.thcPercent,
      },
    ],
    totalWeightGrams: targets.targetSizeGrams,
    targetThcMg: targets.targetThcMg,
    scalePrecisionGrams: targets.scalePrecisionGrams,
    mode: 'closest',
  });

  const result = outcome.result;
  if (
    !isValidWheelBlendResult(
      result,
      targets.targetSizeGrams,
      outcome.targetOutOfRange,
    )
  ) {
    return {
      ok: false,
      message:
        'These two strains cannot produce a valid blend for the current targets.',
    };
  }

  const primaryAlloc = result.allocations[0]!;
  const balancingAlloc = result.allocations[1]!;
  const total = targets.targetSizeGrams;
  return {
    ok: true,
    result,
    weights: {
      primaryWeightGrams: primaryAlloc.grams,
      balancingWeightGrams: balancingAlloc.grams,
      totalWeightGrams: total,
      estimatedThcMg: result.estimatedThcMg,
      roundingDifferenceMg: result.differenceMg,
      primaryProportion: total > 0 ? primaryAlloc.grams / total : 0,
      balancingProportion: total > 0 ? balancingAlloc.grams / total : 0,
    },
  };
}

/**
 * Balancing strains that are labelled and can mathematically solve the
 * current primary + targets combination.
 */
export function filterMathematicallyEligibleBalancers(
  strains: readonly WheelStrainCandidate[],
  primary: WheelStrainCandidate,
  targets: WheelTargets,
): WheelStrainCandidate[] {
  const labelled = filterBalancingLabelEligible(strains, primary.id);
  return labelled.filter((balancer) => {
    const calc = calculateWheelBlend(primary, balancer, targets);
    return calc.ok;
  });
}

/**
 * Surprise Me: random pick from mathematically eligible balancers only.
 */
export function surpriseBalancingStrain(
  eligibleBalancers: readonly WheelStrainCandidate[],
  options: { excludeId?: string | null; random?: RandomFn } = {},
): WheelStrainCandidate | null {
  return pickRandomStrain(eligibleBalancers, options);
}

export function defaultWheelTargets(
  overrides: Partial<WheelTargets> = {},
): WheelTargets {
  return {
    targetSizeGrams:
      overrides.targetSizeGrams ?? CALCULATOR_DEFAULTS.totalWeightGrams,
    targetThcMg: overrides.targetThcMg ?? CALCULATOR_DEFAULTS.targetThcMg,
    scalePrecisionGrams:
      overrides.scalePrecisionGrams ?? CALCULATOR_DEFAULTS.scalePrecisionGrams,
  };
}
